import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  defineMessages,
  useIntl,
} from 'react-intl';
import { Link, To } from 'react-router-dom';
import styled, { css } from 'styled-components';

import {
  ManagerProgressionCategorySlug,
  ManagerProgressionTaskSlug,
  ManagerProgressionTaskState,
  ShopItemType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isAbsolute } from '@sorare/core/src/lib/urls';

import { useDeclareManagerProgressionTask } from 'hooks/useDeclareManagerProgressionTask';

import { Task_ManagerProgressionTask } from './__generated__/index.graphql';
import { useClaimManagerProgressionTask } from './useClaimManagerProgressionTask';
import { useGetTaskAction } from './useGetTaskAction';

const messages = defineMessages({
  managerPoints: {
    id: 'Challenges.Category.Task.managerPoints',
    defaultMessage: 'Manager Points',
  },
});

const rootStyle = css`
  padding: var(--double-unit) var(--triple-unit);
  background-color: var(--c-neutral-200);
  border-radius: var(--double-unit);
`;

const Root = styled.div`
  ${rootStyle}
`;
const ButtonRoot = styled.button`
  ${rootStyle}
  text-align: left;

  &:hover {
    color: var(--c-link-hover);
  }
`;
const ProgressBar = styled.div`
  height: var(--half-unit);
  background-color: var(--c-neutral-300);
  border-radius: 2px;
  overflow: hidden;
`;
const Progress = styled.div`
  height: 100%;
  background-color: var(--c-neutral-1000);

  &.claimed {
    background: linear-gradient(
      84deg,
      #f8d3da 0%,
      #b3a9f4 28.32%,
      #fbe9fb 54.01%,
      #4f94fd 100%
    );
  }
`;
const SpaceBetweenVertical = styled(Vertical)`
  height: 100%;
  justify-content: space-between;
`;
const StyledButton = styled(Button)`
  flex-shrink: 0;
`;

export type GenericSkinItem = {
  id: string;
  pictureUrl: string;
  position: ShopItemType;
};

type Props = {
  task: Task_ManagerProgressionTask;
  category: ManagerProgressionCategorySlug;
  onClaim: ({
    rewards,
    pointsEarned,
  }: {
    rewards: GenericSkinItem[];
    pointsEarned: number;
  }) => void;
  isMe: boolean;
};

const ConditionnalLinkRoot = ({
  taskName,
  categoryName,
  children,
  to,
  onClick,
}: {
  taskName: ManagerProgressionTaskSlug;
  categoryName: ManagerProgressionCategorySlug;
  children: ReactNode;
  to?: To;
  onClick?: () => void;
}) => {
  const track = useEvents();

  const onClickWithTrack = () => {
    track('Click Manager Progression Task', {
      taskName,
      categoryName,
    });
    onClick?.();
  };

  if (to) {
    return (
      <Root
        as={Link}
        to={to}
        {...(isAbsolute(to.toString())
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
        onClick={onClickWithTrack}
      >
        {children}
      </Root>
    );
  }
  if (onClick) {
    return <ButtonRoot onClick={onClickWithTrack}>{children}</ButtonRoot>;
  }

  return <Root>{children}</Root>;
};

export const Task = ({ task, category, onClaim, isMe }: Props) => {
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();
  const [claimManagerProgressionTask] = useClaimManagerProgressionTask();
  const [declareManagerProgressionTask] = useDeclareManagerProgressionTask();
  const { isAndroidApp, isIosApp, postMessage } = useIsMobileApp();

  const declareTask = () => {
    declareManagerProgressionTask({
      variables: {
        name: task.name,
        categoryName: category,
        sport: sport || Sport.FOOTBALL,
      },
    });
  };

  const { onClick, to } = useGetTaskAction(task.name, declareTask);

  if (task.aasmState === ManagerProgressionTaskState.CANCELED) {
    return null;
  }

  const claimTask = async () => {
    const { errors, data } = await claimManagerProgressionTask({
      variables: {
        name: task.name,
        categoryName: category,
        sport: sport || Sport.FOOTBALL,
      },
    });

    if (!errors.length) {
      onClaim({
        rewards: data?.claimManagerProgressionTask?.rewards || [],
        pointsEarned: task.points,
      });
      if (isAndroidApp) postMessage('managerProgressionTaskClaimed');
    }
  };

  const progress =
    task.target === 0 &&
    [
      ManagerProgressionTaskState.COMPLETED,
      ManagerProgressionTaskState.CLAIMED,
    ].includes(task.aasmState)
      ? 1
      : task.progress;
  const target = task.target === 0 ? 1 : task.target;

  const taskDone = [
    ManagerProgressionTaskState.COMPLETED,
    ManagerProgressionTaskState.CLAIMED,
  ].includes(task.aasmState);

  const actualTo = taskDone ? undefined : to;

  const actualOnClick =
    (task.declarative || onClick) && !taskDone
      ? onClick || declareTask
      : undefined;

  const allowClick = isMe && !isIosApp;

  const action = (
    <>
      {task.aasmState === ManagerProgressionTaskState.COMPLETED && (
        <StyledButton size="medium" color="primary" onClick={claimTask}>
          <FormattedMessage {...glossary.claim} />
        </StyledButton>
      )}
      {task.aasmState === ManagerProgressionTaskState.CLAIMED && (
        <Chip
          custom={{
            color: 'var(--c-neutral-600)',
            background: 'rgba(var(--c-rgb-neutral-1000), .1)',
          }}
          label={Label => (
            <Label>
              <FormattedMessage {...glossary.claimed} />
            </Label>
          )}
        />
      )}
      {allowClick && (actualTo || actualOnClick) && (
        <FontAwesomeIcon icon={faChevronRight} />
      )}
    </>
  );

  return (
    <ConditionnalLinkRoot
      taskName={task.name}
      categoryName={category}
      to={allowClick ? actualTo : undefined}
      onClick={allowClick ? actualOnClick : undefined}
    >
      <SpaceBetweenVertical gap={2}>
        <SBHorizontal>
          <Vertical gap={0}>
            <BodyM as="p" bold>
              {task.title}
            </BodyM>
            <BodyS as="p" color="var(--c-neutral-600)">
              {task.description}
            </BodyS>
          </Vertical>
          {isMe && action}
        </SBHorizontal>
        <Vertical gap={1}>
          <ProgressBar>
            <Progress
              className={classNames({
                claimed: task.aasmState === ManagerProgressionTaskState.CLAIMED,
              })}
              style={{ width: `${(progress / target) * 100}%` }}
            />
          </ProgressBar>
          <SBHorizontal>
            <LabelS as="p" bold>
              {progress}
              {' / '}
              {target}
            </LabelS>
            <Horizontal gap={0.5}>
              {task.aasmState === ManagerProgressionTaskState.CLAIMED ? (
                <>
                  <LabelS as="p" color="var(--c-neutral-600)">
                    <FormattedMessage {...glossary.completed} />
                  </LabelS>
                  {task.completedAt && (
                    <LabelS as="p" bold color="var(--c-neutral-600)">
                      <FormattedDate
                        value={task.completedAt}
                        day="2-digit"
                        month="short"
                        year="numeric"
                      />
                    </LabelS>
                  )}
                </>
              ) : (
                <>
                  <LabelS
                    as="p"
                    color="var(--c-neutral-600)"
                    title={formatMessage(messages.managerPoints)}
                  >
                    <FormattedMessage
                      id="Challenges.Category.Task.mp"
                      defaultMessage="MP"
                    />
                  </LabelS>
                  <LabelS as="p" bold color="var(--c-neutral-600)">
                    {task.points}
                  </LabelS>
                </>
              )}
            </Horizontal>
          </SBHorizontal>
        </Vertical>
      </SpaceBetweenVertical>
    </ConditionnalLinkRoot>
  );
};

Task.fragments = {
  managerProgressionTask: gql`
    fragment Task_ManagerProgressionTask on ManagerProgressionTask {
      id
      aasmState
      claimedAt
      completedAt
      name
      title
      description
      progress
      target
      points
      declarative
    }
  ` as TypedDocumentNode<Task_ManagerProgressionTask>,
};
